import { ChangeDetectorRef, Component, OnInit, ViewChild, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActiveModal, TabChangeEvent, TabSetComponent } from '@healthcatalyst/cashmere';

import { ToastNotificationService } from 'src/app/core/services/toast-notification.service';
import {
    parseSearchTokens,
    removeStartingDigits,
    spacedOrUnderscoredWordsToPascalCase,
    spacedOrUnderscoredWordsToSnakeCase
} from 'src/app/shared/utils/string.utils';
import { LegacyConnectionDetails, ConnectionStringService } from 'src/app/core/services/connection-string.service';
import { TenantSource } from 'src/app/shared/models/tenant-source';
import { ConnectionTypesService } from 'src/app/core/services/application/connection-types.service';
import { SystemTypesService } from '../../../../core/services/application/system-types.service';
import { OktaAuthService } from 'src/app/core/services/http/okta-auth.service';
import BaseComponent from 'src/app/shared/components/base.component';
import { Router } from '@angular/router';
import { SelectableItem } from 'src/app/shared/components/grid-bulk-actions/grid-bulk-actions.component';
import { ConnectionFamilyType, ConnectionType, IrtTypes } from 'src/app/shared/models/connection-type';
import { SystemType } from 'src/app/shared/models/system-type';
import { combineLatest, lastValueFrom, map, Observable, shareReplay, take } from 'rxjs';
import { TenantSourceService } from 'src/app/core/services/application/tenant-source.service';
import {
    isLegacyConnectionType,
    isOracleConnectionType,
    isSftpConnectionType,
    isSqlConnectionType,
    isFsConnectionType,
    isFiveTranConnectionType,
    isFileConnectionType,
    isDatabaseConnectionType,
    isLegacyConnectionStringOnlyType
} from 'src/app/shared/utils/connection-type.util';
import { legacyConnectionDetailsValidator } from './validators/legacy-connection-details-validator';
import { createUniqueCaseInsensitiveStringValidator } from './validators/unique-case-insensitive-string-validator';
import { FeatureFlag } from 'src/app/shared/constants/feature-flags';
import { Template } from 'src/app/shared/models/template';
import { TemplateListComponent } from 'src/app/shared/components/template-list/template-list.component';
import {
    devStatusDeprecatedTooltip,
    devStatusDevTooltip,
    devStatusInactiveTooltip,
    devStatusProdTooltip,
    devStatusQATooltip,
    devStatusStaticTooltip
} from 'src/app/shared/constants/tooltips';
import { TemplateSourceTypes } from 'src/app/shared/constants/template-source-types';
import { DynamicSourceConnectionDetailsComponent } from './dynamic-source-connection-details/dynamic-source-connection-details.component';
import { TenantSourceWithConnectionService } from 'src/app/core/services/application/tenant-source-with-connection.service';
import { TenantSelfHostedIrtService } from 'src/app/core/services/application/tenant-self-hosted-irt.service';
import { LoggingService } from 'src/app/shared/services/logging.service';
import { TenantUserRolePermissionsService } from 'src/app/core/services/application/tenant-user-role-permissions.service';
import { sourcesCreate, sourcesEdit } from 'src/app/shared/constants/permissions';

export interface ConnectionTypeOptionVM extends SelectableItem {
    description: string;
    connectionType: string;
    disabled: boolean;
    tooltip: string;
    connectionConfig: ConnectionConfigDetails[] | null;
    connectionFamily: ConnectionFamilyType | null;
    supportedIrtTypes: IrtTypes[] | null;
}

export interface SystemTypeOptionVM extends SelectableItem {
    display: string;
    systemTypeId: number;
}

export interface SourceDevStatusOptionVM {
    name: string;
    description: string;
    value: string;
}

export enum SourceDevStatus {
    Development = 'development',
    QAReview = 'qa-review',
    Production = 'production',
    Inactive = 'inactive',
    Static = 'static',
    Deprecated = 'deprecated'
}

export interface ConnectionConfigDetails {
    label: string;
    masked: boolean;
    required: boolean;
    secretSuffix: string;
    tooltip: string | null;
    type: string | number;
    placeholder?: string;
}

@Component({
    selector: 'cdp-tenant-source-modal',
    templateUrl: './source-modal.component.html',
    styleUrls: ['./source-modal.component.scss'],
    standalone: false
})
export class TenantSourceModalComponent extends BaseComponent implements OnInit {
    @ViewChild('tabSet') tabSet!: TabSetComponent;
    @ViewChild('templateList') templateList!: TemplateListComponent;
    @ViewChild('dynamicForm') dynamicDetailsForm: DynamicSourceConnectionDetailsComponent;

    selectedTabIndex!: number;
    sourceInfoInvalidIndicatorDisplayed!: boolean;
    connectionDetailsMode!: string;
    tenantId!: string;
    tenantSourceForm!: FormGroup;
    systemTypes = new Array<SystemTypeOptionVM>();
    connectionTypes = new Array<ConnectionTypeOptionVM>();
    devStatuses: SourceDevStatusOptionVM[] = [
        {
            name: SourceDevStatus.Development,
            value: SourceDevStatus.Development,
            description: devStatusDevTooltip
        },
        {
            name: 'QA Review',
            value: SourceDevStatus.QAReview,
            description: devStatusQATooltip
        },
        {
            name: SourceDevStatus.Production,
            value: SourceDevStatus.Production,
            description: devStatusProdTooltip
        },
        {
            name: SourceDevStatus.Inactive,
            value: SourceDevStatus.Inactive,
            description: devStatusInactiveTooltip
        },
        {
            name: SourceDevStatus.Static,
            value: SourceDevStatus.Static,
            description: devStatusStaticTooltip
        },
        {
            name: SourceDevStatus.Deprecated,
            value: SourceDevStatus.Deprecated,
            description: devStatusDeprecatedTooltip
        }
    ];
    isEdit!: boolean;
    originalDescription: string;
    selectedTemplate?: Template;

    connectionTypesLoading!: boolean;
    systemTypesLoading!: boolean;
    saveInProgress!: boolean;
    saveErrorMessage!: string;
    public tenantSource!: TenantSource;

    editConnectionStringOnLoadError!: string;
    displayConnectionStringLoadError = false;

    existingSourceDescriptions$: Observable<(string | null)[]>;
    existingTablePrefixes$: Observable<(string | null)[]>;
    readonly featureflags = FeatureFlag;
    saveButtonText = signal('Save');
    selfHostedIrtNumbers: number[] = [];
    availableIrtOptions: number[] = [];
    canCreateSources = false;
    canEditSources = false;

    sourceDescriptionHelp = 'Create a name for this source.';
    devStatusHelp = 'Mark as in development during initial setup, and move to production when ready.';
    connectionKeyHelp =
        'The key for the secret in key vault where the connection information is stored. This value cannot be modified after creation.';
    portHelp =
        'The port on which the hostname is listening to requests. This is required for SFTP connections. If you do not know the port of your SFTP server, please enter 22.';
    connectionStringHelp = 'Will be stored in key vault.';
    connectionTypesHelp =
        'Specify the connection type and IRT (Integration Runtime). Selected template will influence the connection type.';
    tablePrefixHelp = 'The prefix for the source tables. Ex: "Epic_". This value cannot be modified after creation.';
    systemTypesHelp = 'Accurately specifying this helps with entity selection and modeling.';
    dataSourceIdHelp = 'ID assigned to the source in DOS.  It is unique by client but not across clients.';

    connectionTypeDisabledHelp = 'This connection type is not supported for templates.';

    maximumConcurrentConnectionsHelp =
        'The maximum number of concurrent connections established with the source during data transfer.  Currently used by ADF Copy Data only.  May be overridden if set at entity level.';
    connectionInfoInvalidIndicatorDisplayed: boolean;

    constructor(
        private activeModal: ActiveModal,
        private fb: FormBuilder,
        private tenantSourceService: TenantSourceService,
        private toast: ToastNotificationService,
        private connectionStringService: ConnectionStringService,
        private connectionTypesService: ConnectionTypesService,
        private systemTypesService: SystemTypesService,
        private selfHostedIrtService: TenantSelfHostedIrtService,
        oktaAuth: OktaAuthService,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private tenantSourceWithConnectionService: TenantSourceWithConnectionService,
        private loggingService: LoggingService,
        private tenantPermissionService: TenantUserRolePermissionsService
    ) {
        super(oktaAuth);
    }

    get connectionTypeDescriptive(): string {
        if (this.sqlTypeSelected) {
            return 'SQL';
        }

        if (this.oracleTypeSelected) {
            return 'Oracle';
        }

        if (this.sftpTypeSelected) {
            return 'SFTP';
        }

        if (this.fsTypeSelected) {
            return 'FS';
        }

        return '';
    }

    public convertSystemTypesToSelectOptions(systemTypes: SystemType[]): SystemTypeOptionVM[] {
        return systemTypes
            .map(st => {
                return {
                    display: `${st.description}${st.version ? ' (' + st.version + ')' : ''}`,
                    systemTypeId: st.systemTypeId,
                    isSelected: false
                };
            })
            .sort((a, b) => a.display.localeCompare(b.display));
    }

    get connectionType(): string {
        return this.tenantSourceForm.controls['connectionType'].value;
    }

    get irtNumber(): string {
        return this.tenantSourceForm.controls['irtNumber'].value;
    }

    get selectedConnectionTypeObject(): ConnectionTypeOptionVM | undefined {
        return this.connectionTypes.find(ct => ct.connectionType === this.connectionType);
    }

    get showIrtSelect(): boolean {
        return (
            !!this.connectionType &&
            this.selectedConnectionTypeObject?.supportedIrtTypes !== null &&
            this.availableIrtOptions.length > 0
        );
    }

    getIrtOptionLabel(irtNumber: number): string {
        if (irtNumber === 0) {
            return `${irtNumber} - AutoResolve`;
        }

        if (irtNumber === 3) {
            return `${irtNumber} - Managed`;
        }

        return `${irtNumber} - Self Hosted`;
    }

    get sqlTypeSelected(): boolean {
        return isSqlConnectionType(this.connectionType);
    }

    get oracleTypeSelected(): boolean {
        return isOracleConnectionType(this.connectionType);
    }

    get sftpTypeSelected(): boolean {
        return isSftpConnectionType(this.connectionType);
    }

    get fsTypeSelected(): boolean {
        return isFsConnectionType(this.connectionType);
    }
    get fiveTranTypeSelected(): boolean {
        return isFiveTranConnectionType(this.connectionType);
    }

    get hasConnectionStringOnlyOption() {
        return isLegacyConnectionStringOnlyType(this.connectionType);
    }

    // Dynamic connection types and legacy connection types can have defined connectionConfig
    get hasConnectionConfig(): boolean {
        if (this.connectionTypes.length > 0) {
            return (
                this.connectionTypes.filter(ct => this.connectionType === ct.connectionType)[0]?.connectionConfig !==
                null
            );
        }
        return false;
    }

    get dynamicConnectionTypeConfig(): any {
        if (this.hasConnectionConfig) {
            return this.connectionTypes.filter(ct => this.connectionType === ct.connectionType)[0]?.connectionConfig;
        }
        return null;
    }

    get requiresDatabaseName(): boolean {
        return this.sqlTypeSelected;
    }

    // Legacy connection types can toggle between basic input fields and connection string input.
    get isLegacyConnectionType(): boolean {
        return isLegacyConnectionType(this.connectionType);
    }

    get usingConnectionString(): boolean {
        return this.tenantSourceForm?.controls['usingConnectionString'].value && this.isLegacyConnectionType;
    }

    get isConnectionStringError(): boolean {
        return !!this.tenantSource.error;
    }

    get connectionStringErrorMessage(): string {
        return this.tenantSource.error;
    }

    get isUnknownConnectionStringFormat(): boolean {
        return !this.tenantSource.host && !this.tenantSource.user;
    }

    get unknownConnectionStringFormatMessage(): string {
        return 'Current connection details could not be parsed due to connection string being in an unknown format';
    }

    get isLegacyConnectionDetailsUpdate(): boolean {
        return (
            this.isEdit &&
            (this.tenantSourceForm?.controls['host'].dirty ||
                this.tenantSourceForm?.controls['database'].dirty ||
                this.tenantSourceForm?.controls['port'].dirty ||
                this.tenantSourceForm?.controls['user'].dirty ||
                this.tenantSourceForm?.controls['password'].dirty ||
                this.tenantSourceForm?.controls['connectionString'].dirty)
        );
    }

    get isLegacyConnectionDetailsValid(): boolean {
        return (
            this.tenantSourceForm?.controls['host'].valid &&
            this.tenantSourceForm?.controls['database'].valid &&
            this.tenantSourceForm?.controls['port'].valid &&
            this.tenantSourceForm?.controls['user'].valid &&
            this.tenantSourceForm?.controls['password'].valid &&
            this.tenantSourceForm?.controls['connectionString'].valid
        );
    }

    get isDynamicConnectionDetailsUpdate(): boolean {
        return this.isEdit && this.dynamicDetailsForm?.connectionDetailsForm.dirty;
    }

    get portPlaceholderText(): string {
        return !this.sftpTypeSelected ? '8080' : '22';
    }

    get shouldDisplaySourceInfoInvalidIndicator(): boolean {
        const sourceInfoTab = this.isEdit ? 0 : 1;
        const value =
            (this.selectedTabIndex == sourceInfoTab || this.sourceInfoInvalidIndicatorDisplayed) &&
            !this.isSourceInfoValid();
        if (value) {
            this.sourceInfoInvalidIndicatorDisplayed = true;
        }

        return value;
    }

    get displayConnectionDetailsInvalidIndicator(): boolean {
        const connectionDetailsTab = this.isEdit ? 1 : 2;
        const value =
            (this.selectedTabIndex == connectionDetailsTab || this.connectionInfoInvalidIndicatorDisplayed) &&
            (this.dynamicDetailsForm?.connectionDetailsForm.invalid || !this.isLegacyConnectionDetailsValid);
        if (value) {
            this.connectionInfoInvalidIndicatorDisplayed = true;
        }

        return value || this.errorLoadingConnectionStringForEdit;
    }

    get errorLoadingConnectionStringForEdit(): boolean {
        // ignore connection string errors if no connection details are required
        if (!this.hasConnectionConfig && !this.isLegacyConnectionType) {
            return false;
        }

        return this.isEdit && !!this.editConnectionStringOnLoadError;
    }

    get sourceDescriptionControl(): AbstractControl<any, any> {
        return this.tenantSourceForm.controls['sourceDescription'];
    }

    get systemTypeControl(): AbstractControl<any, any> {
        return this.tenantSourceForm.controls['systemType'];
    }

    get statusControl(): AbstractControl<any, any> {
        return this.tenantSourceForm.controls['status'];
    }

    get connectionTypeControl(): AbstractControl<any, any> {
        return this.tenantSourceForm.controls['connectionType'];
    }

    get connectionKeyControl(): AbstractControl<any, any> {
        return this.tenantSourceForm.controls['connectionKey'];
    }

    get tablePrefixControl(): AbstractControl<any, any> {
        return this.tenantSourceForm.controls['tablePrefix'];
    }

    get dataSourceIdControl(): AbstractControl<any, any> {
        return this.tenantSourceForm.controls['dataSourceId'];
    }

    get maximumConcurrentConnectionsControl(): AbstractControl<any, any> {
        return this.tenantSourceForm.controls['maximumConcurrentConnections'];
    }

    get hostControl(): AbstractControl<any, any> {
        return this.tenantSourceForm.controls['host'];
    }

    get portControl(): AbstractControl<any, any> {
        return this.tenantSourceForm.controls['port'];
    }

    get irtNumberControl(): AbstractControl<any, any> {
        return this.tenantSourceForm.controls['irtNumber'];
    }

    get hasConnectionStringError(): boolean {
        return !!this.tenantSourceForm.controls['error'].value;
    }

    get isFieldRequired(): boolean {
        return this.isLegacyConnectionDetailsUpdate || !this.isEdit || this.hasConnectionStringError;
    }

    get isViewOnlyMode(): boolean {
        return (this.isEdit && !this.canEditSources) || (!this.isEdit && !this.canCreateSources);
    }

    ngOnInit(): void {
        this.tenantSource = {
            tenantSourceId: null,
            connectionKey: '',
            connectionString: '',
            connectionType: '',
            description: '',
            systemTypeId: 0,
            tablePrefix: null,
            tenantId: '',
            updatedUser: '',
            dataSourceId: '',
            maximumConcurrentConnections: null
        } as TenantSource;
        this.tenantId = this.activeModal.data.tenantId;

        if (this.activeModal.data.source) {
            this.setExistingSource(this.activeModal.data.source);
            this.editConnectionStringOnLoadError = this.activeModal.data.source.error;
            this.isEdit = true;
        } else {
            this.isEdit = false;
        }

        this.loadExistingSourceInfo();
        this.initForm();
        this.initializeEditPermissions();
        this.loadOptions();
        this.saveInProgress = false;
    }

    private initializeEditPermissions(): void {
        combineLatest([
            this.tenantPermissionService.hasRequiredUserPermissions([sourcesCreate]),
            this.tenantPermissionService.hasAnyRequiredUserPermissions([sourcesCreate, sourcesEdit])
        ])
            .pipe(take(1))
            .subscribe(([hasCreatePermission, hasEditPermission]) => {
                this.canCreateSources = hasCreatePermission;
                this.canEditSources = hasEditPermission;
                if (this.isViewOnlyMode) {
                    this.tenantSourceForm.disable({ emitEvent: false });
                } else {
                    this.tenantSourceForm.enable({ emitEvent: false });

                    // Keep immutable edit fields disabled even when user can edit.
                    if (this.isEdit) {
                        this.tenantSourceForm.controls['connectionKey'].disable({ emitEvent: false });
                        this.tenantSourceForm.controls['tablePrefix'].disable({ emitEvent: false });
                    }
                }
            });
    }

    setExistingSource(existingSource: TenantSource): void {
        this.tenantSource = {
            description: existingSource.description,
            connectionKey: existingSource.connectionKey,
            database: !existingSource.error ? existingSource.database : '',
            host: !existingSource.error ? existingSource.host : '',
            port: existingSource.port,
            user: !existingSource.error ? existingSource.user : '',
            error: existingSource.error,
            tablePrefix: existingSource.tablePrefix,
            systemTypeId: existingSource.systemTypeId,
            connectionType: existingSource.connectionType,
            tenantSourceId: existingSource.tenantSourceId,
            dataSourceId: existingSource.dataSourceId,
            status: existingSource.status,
            maximumConcurrentConnections: existingSource.maximumConcurrentConnections,
            connectionDetails: existingSource.connectionDetails,
            connectionFamily: existingSource.connectionFamily,
            irtNum: existingSource.irtNum
        } as TenantSource;

        this.originalDescription = existingSource.description;
    }

    initForm(): void {
        const legacyConnectionDetails: LegacyConnectionDetails | null = {
            host: this.tenantSource.host,
            port: this.tenantSource.port,
            database: this.tenantSource.database,
            user: this.tenantSource.user,
            password: '',
            error: ''
        };
        this.tenantSourceForm = this.fb.group(
            {
                sourceDescription: new FormControl(
                    this.tenantSource.description,
                    [Validators.required, Validators.pattern('[A-Za-z0-9\\s_]*')],
                    [
                        createUniqueCaseInsensitiveStringValidator(
                            this.existingSourceDescriptions$.pipe(
                                map(descriptions => descriptions.filter(d => d != this.originalDescription))
                            )
                        )
                    ]
                ),
                status: new FormControl(this.tenantSource.status || SourceDevStatus.Development, [Validators.required]),
                connectionKey: new FormControl(
                    {
                        value: this.tenantSource.connectionKey,
                        disabled: this.isEdit
                    },
                    [Validators.required, Validators.pattern('(?!.*--)[A-Za-z][A-Za-z0-9-]*')]
                ),
                connectionType: new FormControl(
                    this.tenantSource.connectionType == '' ? null : this.tenantSource.connectionType,
                    [Validators.required, Validators.min(1)]
                ),
                tablePrefix: new FormControl(
                    {
                        value: this.tenantSource.tablePrefix,
                        disabled: this.isEdit
                    },
                    {
                        validators: [Validators.required, Validators.pattern('[A-Za-z][A-Za-z0-9_]*')],
                        asyncValidators: [createUniqueCaseInsensitiveStringValidator(this.existingTablePrefixes$)]
                    }
                ),
                systemType: new FormControl(
                    this.tenantSource.systemTypeId == 0 ? null : this.tenantSource.systemTypeId,
                    [Validators.required, Validators.min(1)]
                ),
                usingConnectionString: new FormControl(this.initUsingConnectionString(legacyConnectionDetails)),
                tenantSourceId: new FormControl(this.tenantSource.tenantSourceId ?? null),
                dataSourceId: new FormControl(this.tenantSource.dataSourceId),
                maximumConcurrentConnections: new FormControl(this.tenantSource.maximumConcurrentConnections),
                connectionString: new FormControl(this.tenantSource.connectionString),
                host: new FormControl(this.tenantSource.host ? this.tenantSource.host : ''),
                error: new FormControl(this.tenantSource.error ? this.tenantSource.error : ''),
                port: new FormControl(legacyConnectionDetails?.port),
                database: new FormControl(this.tenantSource?.database ? this.tenantSource.database : ''),
                user: new FormControl(this.tenantSource?.user ? this.tenantSource.user : ''),
                password: new FormControl(legacyConnectionDetails?.password ? legacyConnectionDetails.password : ''),
                usingDynamicConnectionType: new FormControl(this.initUsingDynamicConnectionType()),
                irtNumber: new FormControl(this.tenantSource.irtNum ?? null)
            },
            {
                validators: legacyConnectionDetailsValidator
            }
        );
    }

    initUsingConnectionString(legacyConnectionDetails: LegacyConnectionDetails | null): boolean {
        const hasConnectionStringOnlyOption = isLegacyConnectionStringOnlyType(this.tenantSource.connectionType);
        return (
            hasConnectionStringOnlyOption ||
            (this.tenantSource.connectionString != '' &&
                legacyConnectionDetails == null &&
                !this.errorLoadingConnectionStringForEdit) ||
            (this.isEdit && !this.tenantSource.host && !this.tenantSource.error)
        );
    }

    initUsingDynamicConnectionType() {
        const isLegacyType = isLegacyConnectionType(this.tenantSource.connectionType);
        return (this.tenantSource.connectionDetails || !isLegacyType) ?? false;
    }

    isSourceInfoValid(): boolean {
        return (
            (this.tenantSourceForm.controls['sourceDescription'].valid ||
                this.tenantSourceForm.controls['sourceDescription'].status == 'PENDING') &&
            this.tenantSourceForm.controls['systemType'].valid &&
            this.tenantSourceForm.controls['connectionType'].valid &&
            (this.isEdit || this.tenantSourceForm.controls['connectionKey'].valid)
        );
    }

    tabChanged(e: TabChangeEvent): void {
        this.selectedTabIndex = e.index;
    }

    onNameChange(): void {
        this.generateConnectionKey();
        this.generateTablePrefix();
    }

    onConnectionTypeChange(): void {
        // reset invalid connection details icon
        this.connectionInfoInvalidIndicatorDisplayed = false;
        if (this.sftpTypeSelected || this.fsTypeSelected || this.hasConnectionConfig) {
            this.tenantSourceForm.controls['usingConnectionString'].setValue(false);
        } else {
            this.setUsingConnectionStringValue();
        }
        // update tenantSourceForm validation
        if (!this.isLegacyConnectionType) {
            this.tenantSourceForm.controls['usingDynamicConnectionType'].setValue(true);
        } else this.tenantSourceForm.controls['usingDynamicConnectionType'].setValue(false);

        this.updateIrtNumberForConnectionType();

        this.cdr.detectChanges();
    }

    updateIrtNumberForConnectionType() {
        // Update available IRT options based on selected connection type
        this.availableIrtOptions = this.getIrtOptionsForConnectionType(this.connectionType);

        // Reset IRT number if current selection is not available for new connection type
        const currentIrtNumber = this.tenantSourceForm.controls['irtNumber'].value;
        const isCurrentIrtNumberAvailable = this.availableIrtOptions.includes(currentIrtNumber);
        if (!isCurrentIrtNumberAvailable) {
            this.tenantSourceForm.controls['irtNumber'].setValue(null);
        }

        // Update IRT number validator
        if (this.showIrtSelect) {
            this.tenantSourceForm.controls['irtNumber'].setValidators([Validators.required]);
        } else {
            this.tenantSourceForm.controls['irtNumber'].clearValidators();
        }
        this.tenantSourceForm.controls['irtNumber'].updateValueAndValidity();
    }

    setUsingConnectionStringValue(): void {
        const usingConnectionString =
            this.hasConnectionStringOnlyOption ||
            (this.isLegacyConnectionType &&
                this.tenantSource.connectionString != '' &&
                !this.errorLoadingConnectionStringForEdit) ||
            (this.isEdit && !this.tenantSource.host && !this.tenantSource.error);

        this.tenantSourceForm.controls['usingConnectionString'].setValue(usingConnectionString);
    }

    showConnectionStringSelect(): boolean {
        return (
            this.isLegacyConnectionType &&
            !this.sftpTypeSelected &&
            !this.fsTypeSelected &&
            !this.hasConnectionStringOnlyOption
        );
    }

    /**
     * Generate connection key from source description if user has not manually edited.
     * Removes starting digits and then converts to pascal case, using spaces to delimit words.
     */
    generateConnectionKey(): void {
        if (!this.isEdit && !this.connectionKeyControl.dirty) {
            const name = this.tenantSourceForm.controls['sourceDescription'].value;
            let nameFormatted = removeStartingDigits(name);
            nameFormatted = spacedOrUnderscoredWordsToPascalCase(nameFormatted);

            this.connectionKeyControl.setValue(nameFormatted);
        }
    }

    /**
     * Generate table prefix from source description if user has not manually edited.
     * Removes starting digits and then converts to snake case, using spaces to delimit words.
     */
    generateTablePrefix(): void {
        if (!this.isEdit && !this.tablePrefixControl.dirty) {
            const name = this.tenantSourceForm.controls['sourceDescription'].value;
            let nameFormatted = removeStartingDigits(name);
            nameFormatted = spacedOrUnderscoredWordsToSnakeCase(nameFormatted);

            this.tablePrefixControl.setValue(nameFormatted);
        }
    }

    saveIsDisabled(): boolean {
        return (
            this.isViewOnlyMode ||
            !this.tenantSourceForm.valid ||
            this.saveInProgress ||
            (this.isEdit && !this.tenantSourceForm.dirty && !this.dynamicDetailsForm?.connectionDetailsForm.dirty) ||
            this.dynamicDetailsForm?.connectionDetailsForm.invalid
        );
    }

    save(): void {
        if (this.isViewOnlyMode) {
            return;
        }

        this.saveInProgress = true;
        this.saveErrorMessage = '';
        const tenantSourceRequest = {} as TenantSource;
        tenantSourceRequest.tenantId = this.tenantId;
        tenantSourceRequest.description = this.tenantSourceForm.controls['sourceDescription'].value.trim();
        tenantSourceRequest.connectionKey = this.tenantSourceForm.controls['connectionKey'].value;
        tenantSourceRequest.connectionType = this.tenantSourceForm.controls['connectionType'].value;
        tenantSourceRequest.irtNum = this.tenantSourceForm.controls['irtNumber'].value;
        tenantSourceRequest.tablePrefix = this.tenantSourceForm.controls['tablePrefix'].value ?? '';
        tenantSourceRequest.systemTypeId = this.tenantSourceForm.controls['systemType'].value;
        tenantSourceRequest.dataSourceId = this.tenantSourceForm.controls['dataSourceId'].value ?? '';
        tenantSourceRequest.maximumConcurrentConnections = !this.tenantSourceForm.controls[
            'maximumConcurrentConnections'
        ].value
            ? null
            : this.tenantSourceForm.controls['maximumConcurrentConnections'].value;
        tenantSourceRequest.status = this.tenantSourceForm.controls['status'].value ?? '';
        tenantSourceRequest.connectionString =
            this.usingConnectionString && this.tenantSourceForm.controls['connectionString'].value
                ? this.tenantSourceForm.controls['connectionString'].value
                : !this.isEdit
                  ? this.generateConnectionString()
                  : this.isLegacyConnectionDetailsUpdate
                    ? this.generateConnectionString()
                    : null;
        tenantSourceRequest.connectionDetails = this.hasConnectionConfig ? this.getDynamicDetailFormValues() : null;
        tenantSourceRequest.updatedUser = this.oktaUser.username;

        tenantSourceRequest.tenantSourceId = this.tenantSource.tenantSourceId;

        this.tenantSourceService.saveTenantSource(tenantSourceRequest, this.isEdit).subscribe({
            next: data => {
                this.saveInProgress = false;

                let message: string;
                let header: string;

                if (this.isEdit) {
                    message = `Successfully updated source '${tenantSourceRequest.description}'.`;
                    header = 'Updated Source';
                } else {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore: Object is possibly 'null'.
                    const sourceId: number = data.tenantSourceId;
                    if (sourceId) {
                        if (this.selectedTemplate) {
                            this.router.navigate(
                                ['tenant', tenantSourceRequest.tenantId, 'sources', sourceId, 'editentities'],
                                {
                                    state: {
                                        templateId: this.selectedTemplate.templateId,
                                        name: this.selectedTemplate.name
                                    }
                                }
                            );
                        } else {
                            this.router.navigate([
                                'tenant',
                                tenantSourceRequest.tenantId,
                                'sources',
                                sourceId,
                                'entities'
                            ]);
                        }
                    }
                    message = `Saved new source '${tenantSourceRequest.description}'.`;
                    header = 'Created Source';
                }

                this.toast.showSuccess(message, header);
                this.activeModal.close(data);
            },
            error: error => {
                this.saveInProgress = false;

                this.loggingService.logError(error);

                let message = 'Unable to save source. ';
                message +=
                    error.status != 500
                        ? typeof error.error != 'string'
                            ? error.error.message
                            : error.error
                        : `Contact an admin if the problem continues.`;

                this.toast.showError(message, 'Save Failed');
            }
        });
    }

    getDynamicDetailFormValues() {
        const isOriginalConnectionType =
            this.isEdit && this.tenantSourceForm.controls['connectionType'].value === this.tenantSource.connectionType;

        const values = this.dynamicDetailsForm.connectionDetailsForm.value;
        // only send updated values
        if (isOriginalConnectionType && this.tenantSource.connectionDetails != null) {
            const existing = this.tenantSource.connectionDetails;
            const changedValue: Record<string, any> = {};

            Object.keys(values).forEach(key => {
                const newValue = values[key as keyof typeof values];
                const oldValue = existing[key as keyof typeof existing];

                if (newValue !== null && newValue !== oldValue) {
                    changedValue[key] = newValue;
                }
            });
            return changedValue;
        }
        return values;
    }

    generateConnectionString(): string {
        const host = this.tenantSourceForm.controls['host'].value;
        const port = this.tenantSourceForm.controls['port'].value;
        const user = this.tenantSourceForm.controls['user'].value;
        const password = this.tenantSourceForm.controls['password'].value;
        const database = this.tenantSourceForm.controls['database'].value;

        let result = '';

        if (this.sqlTypeSelected) {
            result = this.connectionStringService.createSqlString(host, port, user, password, database);
        } else if (this.oracleTypeSelected) {
            result = this.connectionStringService.createOracleString(host, port, user, password);
        } else if (this.sftpTypeSelected) {
            result = this.connectionStringService.createSftpString(host, port, user, password);
        } else if (this.fsTypeSelected) {
            result = this.connectionStringService.createFsString(host, user, password);
        }

        return result;
    }

    toggleConnectionStringError(): void {
        this.displayConnectionStringLoadError = !this.displayConnectionStringLoadError;
    }

    cancel(): void {
        this.activeModal.dismiss();
    }

    connectionTypeSearchFn(term: string, item: ConnectionTypeOptionVM): boolean {
        const tokens = parseSearchTokens(term);
        const searchIn = `${item.description} ${item.connectionType}`.toLocaleLowerCase();
        return tokens.every(token => searchIn.indexOf(token) > -1);
    }

    onTemplateChanged(data: Template) {
        this.selectedTemplate = data;
        const selectedTemplateSourceType = data.template.tenantSource[0].sourceType;
        this.sourceDescriptionControl.setValue(data.name);
        this.sourceDescriptionControl.markAsTouched();
        this.onNameChange(); // this generates values for controls if name has not been entered manually
        this.saveButtonText.set(`Save and review entities (${data.entityCount})`);
        this.disableOrEnableConnectionTypeOptions(true, selectedTemplateSourceType);
        const connectionTypeControl = this.tenantSourceForm.controls['connectionType'];
        const currentConnectionType = connectionTypeControl.value;
        const currentConnectionFamily = this.getConnectionFamilyFromConnectionType(currentConnectionType);

        if (
            (selectedTemplateSourceType === TemplateSourceTypes.relational &&
                isFileConnectionType(currentConnectionFamily)) ||
            (selectedTemplateSourceType === TemplateSourceTypes.file &&
                isDatabaseConnectionType(currentConnectionFamily))
        ) {
            connectionTypeControl.setValue(null);
            connectionTypeControl.markAsUntouched();
        }
    }

    getConnectionFamilyFromConnectionType(connectionType: string) {
        return this.connectionTypes.filter(ct => ct.connectionType === connectionType)[0]?.connectionFamily;
    }

    isTextTruncated(element: HTMLElement | null): boolean {
        if (!element) {
            return false;
        }
        return element.scrollWidth > element.clientWidth;
    }

    onCreateSource() {
        this.selectedTemplate = undefined;
        this.sourceDescriptionControl.setValue(null);
        this.sourceDescriptionControl.markAsUntouched();
        this.templateList.clear();
        this.disableOrEnableConnectionTypeOptions(false, null);
        this.saveButtonText.set('Save');
        this.tabSet.selectTab(1);
    }

    private async loadOptions() {
        this.systemTypesLoading = true;
        this.connectionTypesLoading = true;
        try {
            const systemTyperesponse = await lastValueFrom(this.systemTypesService.getSystemTypes());
            this.systemTypes = this.convertSystemTypesToSelectOptions(systemTyperesponse);

            const connectionTypeResponse = await this.connectionTypesService.getConnectionTypes();
            this.connectionTypes = this.convertConnectionTypesToSelectOptions(connectionTypeResponse);

            // Load self-hosted IRT numbers for this tenant
            await this.loadSelfHostedIrtNumbers();

            if (this.tenantSource.connectionType) {
                this.availableIrtOptions = this.getIrtOptionsForConnectionType(this.tenantSource.connectionType);
            }
        } catch (e) {
            this.loggingService.logError(e);

            this.systemTypes = [];
            this.toast.showError(
                'Unable to load system type or connection type options. Contact an admin if the problem continues.',
                'Failed to Load Options'
            );
        } finally {
            this.systemTypesLoading = false;
            this.connectionTypesLoading = false;
        }
    }

    private async loadSelfHostedIrtNumbers(): Promise<void> {
        try {
            const response = await lastValueFrom(this.selfHostedIrtService.getTenantSelfHostedIrts(this.tenantId));
            this.selfHostedIrtNumbers = response.map(irt => irt.irtNum);
        } catch (error) {
            this.loggingService.logError(error);
            this.selfHostedIrtNumbers = [];
            this.toast.showWarning(
                'Unable to load available Integration Runtime numbers. Contact an admin if the problem continues.',
                'Warning'
            );
        }
    }

    private getIrtOptionsForConnectionType(connectionType: string): number[] {
        const selectedConnectionType = this.connectionTypes.find(ct => ct.connectionType === connectionType);
        const supportedIrtTypes = selectedConnectionType?.supportedIrtTypes || [];
        const irtOptions: number[] = [];

        supportedIrtTypes.forEach(irtType => {
            switch (irtType.toLowerCase()) {
                case IrtTypes.SelfHosted.toLowerCase():
                    irtOptions.push(...this.selfHostedIrtNumbers);
                    break;
                case IrtTypes.AutoResolve.toLowerCase():
                    irtOptions.push(0);
                    break;
                case IrtTypes.VirtualNetwork.toLowerCase():
                    irtOptions.push(3);
                    break;
            }
        });

        return [...new Set(irtOptions)].sort((a, b) => a - b); // Remove duplicates and sort
    }

    private loadExistingSourceInfo(): void {
        const existingSources$ = this.tenantSourceWithConnectionService
            .getTenantSourcesWithConnection(this.tenantId)
            .pipe(
                map(sources => (Array.isArray(sources) ? sources : [])),
                shareReplay(1)
            );

        this.existingSourceDescriptions$ = existingSources$.pipe(map(sources => sources.map(s => s.description)));
        this.existingTablePrefixes$ = existingSources$.pipe(map(sources => sources.map(s => s.tablePrefix)));

        // preload this when method called
        existingSources$.pipe(take(1)).subscribe();
    }

    public convertConnectionTypesToSelectOptions(connectionTypes: ConnectionType[]): ConnectionTypeOptionVM[] {
        const supportedConnectionTypes = connectionTypes
            .filter(ct => ct.isSupported)
            .map(r => r.connectionType.toLowerCase());
        return connectionTypes
            .filter(
                ct =>
                    // keep if supported OR if it's currently selected
                    supportedConnectionTypes.includes(ct.connectionType.toLowerCase()) ||
                    ct.connectionType.toLowerCase() === this.tenantSource.connectionType?.toLowerCase()
            )
            .map(ct => {
                return {
                    description: ct.description,
                    connectionType: ct.connectionType,
                    isSelected: false,
                    disabled: false,
                    tooltip: '',
                    connectionConfig: ct.configJson,
                    connectionFamily: ct.connectionFamily,
                    supportedIrtTypes: ct.supportedIrtTypes
                };
            })
            .sort((a, b) => a.description.localeCompare(b.description));
    }

    public disableOrEnableConnectionTypeOptions(disabled: boolean, templateSourceType: string | null) {
        this.connectionTypes = this.connectionTypes.map(ct => {
            const hasFileSourceTemplateAndIsFileType =
                templateSourceType === TemplateSourceTypes.file && isFileConnectionType(ct.connectionFamily);
            const hasRelationalSourceTemplateAndIsRelationalType =
                templateSourceType === TemplateSourceTypes.relational && isDatabaseConnectionType(ct.connectionFamily);
            const mappedData = {
                ...ct,
                disabled:
                    !hasRelationalSourceTemplateAndIsRelationalType && !hasFileSourceTemplateAndIsFileType
                        ? disabled
                        : false
            };
            mappedData.tooltip = mappedData.disabled ? this.connectionTypeDisabledHelp : '';
            return mappedData;
        });
    }
}
